import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { HiChevronDown, HiCheck, HiOutlineUser, HiOutlineMail, HiOutlinePhone, HiOutlineLocationMarker, HiOutlineOfficeBuilding, HiOutlineMailOpen } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchSettings } from '../../../admin/settings/controller/settings-controller';
import { updateOrderProduct, createOrder, fetchOrderNewReference } from '../../../admin/orders-controller/order-controller';
import { promoCodeValidationService } from '../../../../services/promoCodeValidationService';
import spinService from '../../../../services/spinService';
import facebookConversionService from '../../../../services/facebookConversionService';
import { useParams, useNavigate } from 'react-router-dom';
import CustomDropdown from './custom-dropdown';
import { isAuthenticated, getCurrentUser } from '../../../../login/controller/login-controller';
import RouletteWheel from '../../../../components/RouletteWheel';
import AuthPopup from '../../../../components/AuthPopup';
import { isStockRequired, UNLIMITED_ORDER_QUANTITY } from '../../../../utils/stock';

// Tunisian cities with codes
const tunisianCities = [
  { value: "TUN", label: "Tunis" },
  { value: "SFX", label: "Sfax" },
  { value: "SOS", label: "Sousse" },
  { value: "KRN", label: "Kairouan" },
  { value: "BIZ", label: "Bizerte" },
  { value: "GBS", label: "Gabes" },
  { value: "AM", label: "Ariana" },
  { value: "GFS", label: "Gafsa" },
  { value: "MNS", label: "Monastir" },
  { value: "BA", label: "Ben Arous" },
  { value: "KSR", label: "Kasserine" },
  { value: "MDN", label: "Medenine" },
  { value: "NAB", label: "Nabeul" },
  { value: "TAN", label: "Tataouine" },
  { value: "BJA", label: "Beja" },
  { value: "JDB", label: "Jendouba" },
  { value: "KEF", label: "Kef" },
  { value: "MHD", label: "Mahdia" },
  { value: "SBZ", label: "Sidi Bouzid" },
  { value: "TZR", label: "Tozeur" },
  { value: "SLI", label: "Siliana" },
  { value: "KBL", label: "Kebili" },
  { value: "ZAG", label: "Zaghouan" },
  { value: "MAN", label: "Manouba" },
];

const ProductInfo = ({
  product,
  selectedVariant,
  quantity,
  onQuantityChange,
  onVariantChange,
  onAddToCart,
  onImageChange
}) => {
  // State for checkout form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: 'TUN',
    codePromo: '',
    notes: '',
    paymentMethod: 'cash',
    terms: true
  });

  // NEW - Add promo code state
  const [promoCodeState, setPromoCodeState] = useState({
    isValidating: false,
    isValid: false,
    discount: 0,
    validatedCode: null,
    type: null, // 'spin' | 'promo'
    pourcentage: 0,
    error: null
  });

  const [errors, setErrors] = useState({});
  const [formComplete, setFormComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddedToCart, setShowAddedToCart] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderReference, setOrderReference] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [showCouponPicker, setShowCouponPicker] = useState(false);
  const [myCoupons, setMyCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [showRoulette, setShowRoulette] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  // Pack product state
  const [selectedPack, setSelectedPack] = useState(null); // null = no pack, or the pack object
  const [packVariantSelections, setPackVariantSelections] = useState([]); // array of variant ids for each unit in the pack
  const navigate = useNavigate();

  // Refs for abandoned order tracking
  const phoneInputRef = useRef(null);
  const abandonedOrderCreatedRef = useRef(false);
  const referenceRef = useRef(null);
  const successfulSubmissionRef = useRef(false); // New ref for tracking successful submissions
  const { platformid, depotid } = useParams();
  // Extract variant names from product variants
  const variants = useMemo(() => {
    if (!product || !product.productVariants || product.productVariants.length === 0) {
      return [];
    }

    // Return array of variant objects with id and name
    return product.productVariants.map(variant => ({
      id: variant.id,
      name: variant.name,
      reference: variant.reference,
      stock: variant.stock || 0,
      displayName: variant.name // Use full name directly
    }));
  }, [product]);

  // StockRequis = false: orderable whatever the stock, and the stock is never shown
  const stockRequired = isStockRequired(product);
  const cannotOrder = product?.isVariant
    ? (!selectedVariant || (stockRequired && selectedVariant.stock <= 0))
    : (stockRequired && (!product?.stock || product.stock <= 0));
  const maxOrderQuantity = !stockRequired
    ? UNLIMITED_ORDER_QUANTITY
    : (product?.isVariant ? (selectedVariant ? selectedVariant.stock : 0) : (product?.stock ?? 0));

  // Fetch settings including delivery fee on component mount
  useEffect(() => {
    const getSettings = async () => {
      try {
        const settings = await fetchSettings();
        if (settings && settings.fraisDeLivraison !== undefined) {
          setDeliveryFee(settings.fraisDeLivraison);
        }
      } catch (error) {
        console.error('Error fetching delivery fee:', error);
      }
    };

    getSettings();
  }, []);

  // Track Facebook ViewContent so visitors from Meta ads are counted on this product.
  // Pixel + token come from the product's marque (fallback: appsettings/env).
  useEffect(() => {
    if (!product?.id) return;
    if (product.marque?.pixelId) {
      facebookConversionService.initPixel(product.marque.pixelId);
    }
    facebookConversionService.trackViewContent({
      productId: product.id,
      productName: product.name,
      value: product.prixVenteTTC || 0,
      currency: 'TND',
      contentType: 'product',
      pixelId: product.marque?.pixelId,
    });
  }, [product?.id]);

  // Get reference for order
  useEffect(() => {
    const getOrderReference = async () => {
      if (!orderReference) {
        try {
          const reference = await fetchOrderNewReference();
          setOrderReference(reference);
          referenceRef.current = reference && typeof reference === 'object' && reference.numero
            ? reference.numero
            : `CMD-${Date.now()}`;
        } catch (error) {
          console.error('Error fetching order reference:', error);
          // Generate a fallback reference if API fails
          const date = new Date();
          const timestamp = date.getTime();
          setOrderReference(`CMD-${timestamp}`);
          referenceRef.current = `CMD-${timestamp}`;
        }
      }
    };

    getOrderReference();
  }, [orderReference]);

  // Get label for stock display
  const getStockLabel = () => {
  // For products without variants, use product.stock directly
  if (!product.isVariant || !product.productVariants || product.productVariants.length === 0) {
    if (!product.stock || product.stock === 0) return 'Rupture de stock';
    if (product.stock <= 5) return `Stock limité (${product.stock})`;
    return `En stock (${product.stock})`;
  }
  
  // For products with variants, use selectedVariant.stock
  if (!selectedVariant) return 'Indisponible';
  if (selectedVariant.stock === 0) return 'Rupture de stock';
  if (selectedVariant.stock <= 5) return `Stock limité (${selectedVariant.stock})`;
  return `En stock (${selectedVariant.stock})`;
};

  // Set color for stock status
  const stockLabelColor = () => {
  // For products without variants, use product.stock directly
  if (!product.isVariant || !product.productVariants || product.productVariants.length === 0) {
    if (!product.stock || product.stock === 0) return 'text-red-600';
    if (product.stock <= 5) return 'text-amber-600';
    return 'text-emerald-600';
  }
  
  // For products with variants, use selectedVariant.stock
  if (!selectedVariant || selectedVariant.stock === 0) return 'text-red-600';
  if (selectedVariant.stock <= 5) return 'text-amber-600';
  return 'text-emerald-600';
};

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const cartNotification = {
    hidden: { opacity: 0, scale: 0.8, y: -10 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2
      }
    }
  };

  // Promo handling - support multiple API shapes and types
  const parseBool = (v) => v === true || v === 'true' || v === 1 || v === '1';
  const rawPrice = Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0);
  const promoPriceVal = Number(product?.prixVentePromo ?? product?.PrixVentePromo ?? 0);
  const promoActive = parseBool(product?.enPromo ?? product?.EnPromo) || (promoPriceVal > 0);
  const promoDiscountPercentage = (promoActive && rawPrice > 0 && promoPriceVal > 0)
    ? Math.round(((rawPrice - promoPriceVal) / rawPrice) * 100)
    : 0;

  // Calculate discount if needed
  const originalPrice = product?.prixAchatTTC || 0; // Use actual price if available
  const hasDiscount = originalPrice > 0 && originalPrice > product?.prixVenteTTC;
  const discountPercentage = hasDiscount
    ? Math.round(((originalPrice - product?.prixVenteTTC) / originalPrice) * 100)
    : 0;

  // Unit price (use promo price when active)
  const productUnitPrice = promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0);
  // Pack pricing: if a pack is selected, use pack unit price and quantity
  const effectiveUnitPrice = selectedPack ? selectedPack.prixUnitaireTTC : productUnitPrice;
  const effectiveQuantity = selectedPack ? selectedPack.qte : quantity;
  // Calculate if this product qualifies for free delivery
  const productTotal = (effectiveUnitPrice || 0) * effectiveQuantity;
  // Dev-only debug logs to help trace price calculations
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('ProductInfo pricing:', { rawPrice, promoPriceVal, promoActive, productUnitPrice, effectiveUnitPrice, productTotal, effectiveQuantity, selectedPack });
  }
  // Free delivery is configured per product: enabled via a flag with a minimum quantity.
  const freeDeliveryEnabled = product?.livraisonGratuit === true || product?.LivraisonGratuit === true;
  const freeDeliveryThreshold = Number(product?.livraisonGratuitQuantite ?? product?.LivraisonGratuitQuantite ?? 0);
  const isFreeDelivery = freeDeliveryEnabled && freeDeliveryThreshold > 0 && effectiveQuantity >= freeDeliveryThreshold;
  const finalDeliveryFee = isFreeDelivery ? 0 : deliveryFee;
  const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;

  // Apply promo discount to TTC and recalculate HT and TVA
  const discountedTTC = Math.max(0, productTotal - promoDiscount);
  const finalHT = discountedTTC / 1.19; // Recalculate HT from discounted TTC
  const finalTVA = discountedTTC - finalHT; // Recalculate TVA from discounted TTC
  const grandTotal = discountedTTC + finalDeliveryFee;

  // Handle clicking a variant
  const handleVariantSelect = (variantId) => {
    onVariantChange(variantId);

    // Update the main image if this variant has an image
    const variant = product.productVariants.find(v => v.id === variantId);
    if (variant && variant.imageUrl && variant.imageUrl.length > 0 && variant.imageUrl[0]) {
      onImageChange(variant.imageUrl[0]);
    }
  };

  // Handle pack selection
  const handlePackSelect = (pack) => {
    if (selectedPack && selectedPack.qte === pack.qte) {
      // Deselect
      setSelectedPack(null);
      setPackVariantSelections([]);
    } else {
      setSelectedPack(pack);
      // Initialize variant selections for each unit in the pack
      if (product.isVariant && product.productVariants && product.productVariants.length > 0) {
        setPackVariantSelections(Array(pack.qte).fill(null));
      } else {
        setPackVariantSelections([]);
      }
    }
  };

  // Handle selecting a variant for a specific unit in the pack
  const handlePackVariantSelect = (unitIndex, variantId) => {
    setPackVariantSelections(prev => {
      const updated = [...prev];
      updated[unitIndex] = variantId;
      return updated;
    });
  };

  // Phone input change handler with abandoned order creation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Create a special case for phone field
    if (name === 'phone') {
      const phoneValue = value;
      
      // Update form data with the new phone number
      setFormData(prev => ({
        ...prev,
        phone: phoneValue
      }));
      
      // Clear validation error when user types
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: null }));
      }
      
      // Create abandoned order when phone number reaches exactly 8 digits for the first time
      // FIXED: Remove the selectedVariant requirement
      if (phoneValue.length === 8 && !abandonedOrderCreatedRef.current && referenceRef.current) {
        createAbandonedOrder(phoneValue);
      }
    } else if (name === 'codePromo') {
      // Handle promo code changes
      setFormData(prev => ({
        ...prev,
        codePromo: value
      }));
      
      // Reset promo code state when user types
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        error: null
      });
      
      // Clear validation error when user types
      if (errors.codePromo) {
        setErrors(prev => ({ ...prev, codePromo: null }));
      }
    } else {
      // Handle other form fields normally
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));

      // Persist identity fields to Facebook Advanced Matching as soon as they
      // are entered so every subsequent event carries hashed fn/ln/ct/em.
      if (['firstName', 'lastName', 'city', 'email'].includes(name) && value) {
        try {
          facebookConversionService.setCustomer({ [name]: value, country: 'TN' });
        } catch { /* ignore */ }
      }

      // Clear validation error when user types
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  // Create a separate function to handle abandoned order creation
  const createAbandonedOrder = async (phoneInput) => {
    try {
      // Set the flag to prevent duplicate orders
      abandonedOrderCreatedRef.current = true;
      console.log("Creating abandoned order from phone entry...");

  // Calculate prices - Apply promo discount to TTC then recalculate
  const abanProductPrice = selectedPack ? selectedPack.prixUnitaireTTC : (promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0));
  const abanQty = selectedPack ? selectedPack.qte : quantity;
  const totalPrice = abanProductPrice * abanQty;
      // Free delivery is driven by the product's livraison gratuite setting.
      const finalDeliveryFee = (freeDeliveryEnabled && freeDeliveryThreshold > 0 && abanQty >= freeDeliveryThreshold) ? 0 : deliveryFee;
      
      // Apply promo discount to TTC total
      const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;
      const discountedTTC = Math.max(0, totalPrice - promoDiscount);
      
      // Recalculate HT and TVA from discounted TTC
      const finalHT = discountedTTC / 1.19;
      const finalTVA = discountedTTC - finalHT;
      
      const orderTotal = discountedTTC + finalDeliveryFee;

      // Build order lines: if pack is selected and product has variants, create one line per unit
      const buildOrderLines = () => {
        if (selectedPack && product.isVariant && packVariantSelections.length > 0) {
          const remisePerUnit = productUnitPrice - selectedPack.prixUnitaireTTC;
          return packVariantSelections.map((variantId) => ({
            productVariantId: variantId || selectedVariant?.id || null,
            productId: product?.id || null,
            quantite: 1,
            prixUnitaire: Number(selectedPack.prixUnitaireTTC.toFixed(2)),
            totalTTC: Number(selectedPack.prixUnitaireTTC.toFixed(2)),
            totalHt: Number((selectedPack.prixUnitaireTTC / 1.19).toFixed(2)),
            totalTva: Number((selectedPack.prixUnitaireTTC - selectedPack.prixUnitaireTTC / 1.19).toFixed(2)),
            totalRemise: Number(remisePerUnit.toFixed(2)),
            tauxTva: 19,
            tauxRemise: 0
          }));
        }
        // Default: single line
        return [{
          productVariantId: selectedVariant?.id || null,
          productId: product?.id || null,
          quantite: abanQty,
          prixUnitaire: Number(abanProductPrice.toFixed(2)),
          totalTTC: Number(discountedTTC.toFixed(2)),
          totalHt: Number(finalHT.toFixed(2)),
          totalTva: Number(finalTVA.toFixed(2)),
          totalRemise: Number(promoDiscount.toFixed(2)),
          tauxTva: 19,
          tauxRemise: 0
        }];
      };

      // Prepare abandoned order data
      const abandonedOrderData = {
        entet: {
          dateBonCommande: new Date().toISOString(),
          reference: referenceRef.current,
          utilisateurId: null,
          totalTTC: Number(orderTotal.toFixed(2)),
          totalHt: Number(finalHT.toFixed(2)),
          totalTva: Number(finalTVA.toFixed(2)),
          totalRemise: Number(promoDiscount.toFixed(2)),
          // Spin coupons are not PromoCodes: the backend would fail looking them up
          codePromo: (promoCodeState.isValid && promoCodeState.type === 'promo') ? promoCodeState.validatedCode.code : null,
          promoDiscount: Number(promoDiscount.toFixed(2)),
          source: "web_store",
          etat: "abandoned",
          fraisDeLivraison: Number(finalDeliveryFee.toFixed(2)),
          plateformId: platformid == 0 ? null : platformid,
          depotId: depotid && depotid != 0 ? Number(depotid) : null,
          clientFirstName: "",
          clientLastName: "",
          clientNumber: phoneInput,
          clientAdresse: "",
          clientEmail: "",
          ville: ""
        },
        line: buildOrderLines()
      };

      // Use the createOrder API call
      const res = await createOrder(abandonedOrderData);
      setOrderId(res.id);
      console.log("Abandoned order created successfully");

      // Track InitiateCheckout + Lead (fire-and-forget; pixel handled server-side).
      try {
        facebookConversionService.setCustomer({
          phone: phoneInput,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          city: formData.city || undefined,
          email: formData.email || undefined,
          country: 'TN',
        });
      } catch { /* ignore */ }
      facebookConversionService.trackInitiateCheckout(
         { eventId: `initiatecheckout-${res.id}`, phone: phoneInput, value: orderTotal, currency: 'TND', productId: product.id, pixelId: product.marque?.pixelId },
      );
      facebookConversionService.trackLead(
        { eventId: `lead-${res.id}`, phone: phoneInput, value: orderTotal, currency: 'TND', productId: product.id, pixelId: product.marque?.pixelId },
      );
    } catch (error) {
      console.error("Failed to create abandoned order:", error);
    }
  };

  // Coupon picker
  const openCouponPicker = async () => {
    setShowCouponPicker(true);
    if (myCoupons.length === 0) {
      setCouponsLoading(true);
      try {
        const data = await spinService.getMyCoupons();
        setMyCoupons(Array.isArray(data) ? data : []);
      } catch {
        setMyCoupons([]);
      } finally {
        setCouponsLoading(false);
      }
    }
  };

  const applyCoupon = async (coupon) => {
    setShowCouponPicker(false);
    const unitPrice = promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0);
    const spinDiscount = Number((unitPrice * coupon.pourcentage / 100).toFixed(2));
    setPromoCodeState({
      isValidating: false,
      isValid: true,
      discount: spinDiscount,
      validatedCode: { code: coupon.code },
      type: 'spin',
      pourcentage: coupon.pourcentage,
      error: null
    });
  };

  // NEW - Promo code validation function (tries spin coupon first, then regular promo code)
  const validatePromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        type: null,
        pourcentage: 0,
        error: null
      });
      return;
    }

    setPromoCodeState(prev => ({
      ...prev,
      isValidating: true,
      error: null
    }));

    try {
      // Try spin coupon first
      const spinResult = await spinService.validateCoupon(code.trim());
      if (spinResult && spinResult.pourcentage != null) {
        // It's a valid spin coupon — discount applies to 1 unit
        const unitPrice = promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0);
        const spinDiscount = Number((unitPrice * spinResult.pourcentage / 100).toFixed(2));
        setPromoCodeState({
          isValidating: false,
          isValid: true,
          discount: spinDiscount,
          validatedCode: { code: code.trim() },
          type: 'spin',
          pourcentage: spinResult.pourcentage,
          error: null
        });
        setErrors(prev => ({ ...prev, codePromo: null }));
        return;
      }
    } catch {
      // Not a spin coupon, fall through to promo code check
    }

    try {
      const result = await promoCodeValidationService.checkPromoCode(code.trim());
      
      if (result.success) {
        setPromoCodeState({
          isValidating: false,
          isValid: true,
          discount: Number(result.data.montant) || 0,
          validatedCode: { montant: result.data, code: code.trim() },
          type: 'promo',
          pourcentage: 0,
          error: null
        });
        // Drop a stale "invalide" error set by a submit that raced the validation
        setErrors(prev => ({ ...prev, codePromo: null }));
      } else {
        setPromoCodeState({
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          type: null,
          pourcentage: 0,
          error: result.error
        });
      }
    } catch {
      setPromoCodeState({
        isValidating: false,
        isValid: false,
        discount: 0,
        validatedCode: null,
        type: null,
        pourcentage: 0,
        error: 'Erreur lors de la validation du code promo'
      });
    }
  };

  // NEW - Handle promo code blur
  const handlePromoCodeBlur = () => {
    const promoCode = formData.codePromo;
    if (promoCode && promoCode.trim() !== '') {
      validatePromoCode(promoCode);
    }
  };

  // Validation function
  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName) newErrors.firstName = 'Le prénom est requis';
    if (!formData.lastName) newErrors.lastName = 'Le nom est requis';
    if (!formData.phone) newErrors.phone = 'Le numéro de téléphone est requis';
    if (!formData.address) newErrors.address = 'L\'adresse est requise';
    if (!formData.city) newErrors.city = 'La ville est requise';

    // Phone validation (simple Tunisian phone number check)
    if (formData.phone && !/^[2-9]\d{7}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide (doit contenir 8 chiffres et commencer par 2-9)';
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // NEW - Promo code validation
    if (formData.codePromo && formData.codePromo.trim() !== '' && !promoCodeState.isValid) {
      // Clicking "Commander" blurs the input, which starts validation just before submit
      newErrors.codePromo = promoCodeState.isValidating
        ? 'Vérification du code promo en cours, veuillez réessayer'
        : (promoCodeState.error || 'Code promo invalide');
    }

    // Terms agreement
    if (!formData.terms) {
      newErrors.terms = 'Vous devez accepter les conditions générales';
    }

    // Pack variant selection validation
    if (selectedPack && product.isVariant && product.productVariants && product.productVariants.length > 0) {
      const missingSelections = packVariantSelections.filter(v => !v);
      if (missingSelections.length > 0) {
        newErrors.packVariants = `Veuillez sélectionner une option pour chaque unité du pack (${missingSelections.length} restante(s))`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle order submission
  const handleOrderSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return; // Guard against double-click / double submit
    }

    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setOrderError(null);

      // ADDED: Check promo code availability before submission
      if (promoCodeState.isValid && promoCodeState.validatedCode) {
        if (promoCodeState.type === 'spin') {
          // Re-validate spin coupon is still usable
          try {
            const spinCheck = await spinService.validateCoupon(promoCodeState.validatedCode.code);
            if (!spinCheck || spinCheck.pourcentage == null) {
              setOrderError('Coupon déjà utilisé ou expiré. Veuillez le retirer.');
              setPromoCodeState({ isValidating: false, isValid: false, discount: 0, validatedCode: null, type: null, pourcentage: 0, error: 'Coupon invalide ou expiré' });
              setFormData(prev => ({ ...prev, codePromo: '' }));
              return;
            }
          } catch {
            setOrderError('Erreur lors de la vérification du coupon.');
            return;
          }
        } else {
          // Regular promo code re-check
          try {
            const checkoutResult = await promoCodeValidationService.checkPromoCode(promoCodeState.validatedCode.code);
            if (!checkoutResult.success) {
              setOrderError(`Code promo non disponible: ${checkoutResult.error}`);
              setPromoCodeState({ isValidating: false, isValid: false, discount: 0, validatedCode: null, type: null, pourcentage: 0, error: checkoutResult.error });
              setFormData(prev => ({ ...prev, codePromo: '' }));
              return;
            }
          } catch {
            setOrderError('Erreur lors de la vérification du code promo. Veuillez réessayer.');
            return;
          }
        }
      }

  // Calculate prices - Apply promo discount to TTC then recalculate
  const submitProductPrice = selectedPack ? selectedPack.prixUnitaireTTC : (promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0));
  const submitQty = selectedPack ? selectedPack.qte : quantity;
  const totalPrice = submitProductPrice * submitQty;
      // Free delivery is driven by the product's livraison gratuite setting.
      const isFreeShipping = freeDeliveryEnabled && freeDeliveryThreshold > 0 && submitQty >= freeDeliveryThreshold;
      const finalDeliveryFee = isFreeShipping ? 0 : deliveryFee;
      
      // Apply promo discount to TTC total
      const promoDiscount = promoCodeState.isValid ? promoCodeState.discount : 0;
      const discountedTTC = Math.max(0, totalPrice - promoDiscount);
      
      // Recalculate HT and TVA from discounted TTC
      const finalHT = discountedTTC / 1.19;
      const finalTVA = discountedTTC - finalHT;
      
      const orderTotal = discountedTTC + finalDeliveryFee;

      // If we have an abandoned order ID, update it instead of creating a new one
      if (orderId) {
        // Build pack lines for multi-line pack orders
        const buildPackLines = () => {
          if (selectedPack && product.isVariant && packVariantSelections.length > 0) {
            const remisePerUnit = (Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0)) - selectedPack.prixUnitaireTTC;
            return packVariantSelections.map((variantId) => ({
              productVariantId: variantId || selectedVariant?.id || null,
              productId: product?.id || null,
              quantite: 1,
              prixUnitaire: Number(selectedPack.prixUnitaireTTC.toFixed(2)),
              totalTTC: Number(selectedPack.prixUnitaireTTC.toFixed(2)),
              totalHt: Number((selectedPack.prixUnitaireTTC / 1.19).toFixed(2)),
              totalTva: Number((selectedPack.prixUnitaireTTC - selectedPack.prixUnitaireTTC / 1.19).toFixed(2)),
              totalRemise: Number(remisePerUnit.toFixed(2)),
            }));
          }
          if (selectedPack && !product.isVariant) {
            const remisePerUnit = (Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0)) - selectedPack.prixUnitaireTTC;
            return [{
              productVariantId: selectedVariant?.id || null,
              productId: product?.id || null,
              quantite: selectedPack.qte,
              prixUnitaire: Number(selectedPack.prixUnitaireTTC.toFixed(2)),
              totalTTC: Number((selectedPack.prixUnitaireTTC * selectedPack.qte).toFixed(2)),
              totalHt: Number((selectedPack.prixUnitaireTTC * selectedPack.qte / 1.19).toFixed(2)),
              totalTva: Number((selectedPack.prixUnitaireTTC * selectedPack.qte - selectedPack.prixUnitaireTTC * selectedPack.qte / 1.19).toFixed(2)),
              totalRemise: Number((remisePerUnit * selectedPack.qte).toFixed(2)),
            }];
          }
          return null;
        };

        const packLines = buildPackLines();

        const updatedOrderData = {
          id: orderId,
          clientFirstName: formData.firstName,
          clientLastName: formData.lastName,
          clientEmail: formData.email || "",
          clientAdresse: formData.address,
          clientNumber: formData.phone,
          ville: formData.city,
          etat: "pending",
          fraisDeLivraison: Number(finalDeliveryFee.toFixed(2)),
          productVariantId: selectedVariant?.id || null,
          quantite: submitQty,
          totalTTC: Number(orderTotal.toFixed(2)),
          tauxTva: 19,
          totalHt: Number(finalHT.toFixed(2)),
          totalTva: Number(finalTVA.toFixed(2)),
          totalRemise: Number(promoDiscount.toFixed(2)),
          tauxRemise: 0,
          codePromo: (promoCodeState.isValid && promoCodeState.type === 'promo') ? promoCodeState.validatedCode.code : null,
          promoDiscount: Number(promoDiscount.toFixed(2)),
          plateformId: platformid || 0,
          depotId: depotid && depotid != 0 ? Number(depotid) : null,
          packLines: packLines,
        };

        // Update the existing order
        const response = await updateOrderProduct(updatedOrderData);

        // Mark spin coupon as used AFTER successful order (200).
        // Regular promo codes are counted by the backend when the order is patched.
        if (promoCodeState.isValid && promoCodeState.type === 'spin' && promoCodeState.validatedCode) {
          await spinService.useCoupon(promoCodeState.validatedCode.code);
        }
        
        // Handle successful order update
        setOrderSuccess(true);
        successfulSubmissionRef.current = true;

        // Track Facebook Purchase ONLY here: the order was validated AND the
        // update request succeeded (updateOrderProduct throws on failure).
        // Deterministic event_id per order: Meta dedups any repeat within 48h.
        facebookConversionService.trackPurchase({
          eventId: `purchase-${orderId}`,
          orderId: orderId,
          value: orderTotal,
          currency: 'TND',
          pixelId: product.marque?.pixelId,
          items: [{
            productId: product.id,
            id: product.id,
            quantity: submitQty,
            price: effectiveUnitPrice,
          }],
          customer: {
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
            city: formData.city,
            country: 'TN',
          },
        });

        // Reset form after successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: 'TUN',
          codePromo: '',
          notes: '',
          paymentMethod: 'cash',
          terms: true
        });

        // Reset promo code state
        setPromoCodeState({
          isValidating: false,
          isValid: false,
          discount: 0,
          validatedCode: null,
          type: null,
          pourcentage: 0,
          error: null
        });

        // Redirect to thank you page with order details
        navigate(`/order-confirmation/${orderId}`, {
          state: {
            orderDetails: response
          }
        });
      } else {
        // If somehow we don't have an orderId, show an error
        setOrderError('Erreur lors de la mise à jour de la commande. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      setOrderError('Une erreur est survenue lors de la mise à jour de votre commande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render variant options - UPDATED to handle both single and multi-type variants
const renderVariantOptions = () => {
  if (!product || !product.productVariants || product.productVariants.length === 0) {
    return null;
  }

  // Check if variants follow the "Color - Size" pattern or are single-type
  const hasMultiTypeVariants = product.productVariants.some(variant => 
    variant.name.includes(' - ')
  );

  if (hasMultiTypeVariants) {
    // Handle multi-type variants (Color - Size format)
    const uniqueValues = {};
    
    product.productVariants.forEach(variant => {
      const parts = variant.name.split(' - ');
      if (parts.length === 2) {
        const [color, size] = parts;
        if (!uniqueValues['color']) uniqueValues['color'] = new Set();
        if (!uniqueValues['size']) uniqueValues['size'] = new Set();
        uniqueValues['color'].add(color);
        uniqueValues['size'].add(size);
      }
    });

    const colors = Array.from(uniqueValues['color'] || []);
    const sizes = Array.from(uniqueValues['size'] || []);

    // Helper to check if a combination exists and is in stock
    const getCombinationStatus = (color, size) => {
      const variant = product.productVariants.find(v =>
        v.name === `${color} - ${size}`
      );
      if (!variant) return 'unavailable';
      if (stockRequired && variant.stock <= 0) return 'outOfStock';
      return 'available';
    };

    const currentSelection = selectedVariant ? selectedVariant.name.split(' - ') : ['', ''];
    const [selectedColor, selectedSize] = currentSelection;

    return (
      <div className="space-y-6">
        {/* Color selection */}
        {colors.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700">Couleur</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {colors.map(color => {
                const hasAvailableVariant = sizes.some(size =>
                  getCombinationStatus(color, size) === 'available'
                );

                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      const validSize = sizes.find(size =>
                        getCombinationStatus(color, size) === 'available'
                      ) || sizes[0];

                      const targetVariant = product.productVariants.find(v =>
                        v.name === `${color} - ${validSize}`
                      );

                      if (targetVariant) {
                        onVariantChange(targetVariant.id);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm border rounded-md ${color === selectedColor
                        ? 'border-[#174540] bg-[#174540] text-white'
                        : hasAvailableVariant
                          ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    disabled={!hasAvailableVariant}
                  >
                    {color}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Size selection */}
        {sizes.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700">Taille</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {sizes.map(size => {
                const status = getCombinationStatus(selectedColor, size);
                const isAvailable = status === 'available';
                const isSelected = size === selectedSize;

                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => {
                      if (!isAvailable) return;

                      const targetVariant = product.productVariants.find(v =>
                        v.name === `${selectedColor} - ${size}`
                      );

                      if (targetVariant) {
                        onVariantChange(targetVariant.id);
                      }
                    }}
                    className={`px-3 py-1.5 text-sm border rounded-md ${isSelected
                        ? 'border-[#174540] bg-[#174540] text-white'
                        : isAvailable
                          ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    disabled={!isAvailable}
                  >
                    {size}
                    {status === 'outOfStock' && <span className="ml-1 text-xs">(Épuisé)</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  } else {
    // Handle single-type variants (like BMW, KIAx, VW)
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-medium text-gray-700">Modèle</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {product.productVariants.map(variant => {
              const isSelected = selectedVariant && selectedVariant.id === variant.id;
              const isAvailable = !stockRequired || variant.stock > 0;

              return (
                <button
                  key={variant.id}
                  type="button"
                  onClick={() => {
                    if (isAvailable) {
                      onVariantChange(variant.id);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm border rounded-md ${isSelected
                      ? 'border-[#174540] bg-[#174540] text-white'
                      : isAvailable
                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  disabled={!isAvailable}
                >
                  {variant.name}
                  {!isAvailable && <span className="ml-1 text-xs">(Épuisé)</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
};

  if (!product) return null;

  return (
    <div className="space-y-6">
      {/* Product name and stock info - Hidden on mobile */}
      <div className="hidden md:block">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{product.name}</h1>

        {product.reference && (
          <p className="mt-1 text-sm text-gray-500">
            Réf: {selectedVariant ? selectedVariant.reference : product.reference}
          </p>
        )}
      </div>

      {/* Pricing */}
      <div className="flex items-center">
        {promoActive ? (
          <>
            <p className="text-2xl font-bold text-gray-900 mr-3">{promoPriceVal.toFixed(2)} TND</p>
            <p className="text-lg text-gray-500 line-through">{rawPrice.toFixed(2)} TND</p>
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
              -{promoDiscountPercentage}%
            </span>
          </>
        ) : hasDiscount ? (
          <>
            <p className="text-2xl font-bold text-gray-900 mr-3">{product.prixVenteTTC?.toFixed(2)} TND</p>
            <p className="text-lg text-gray-500 line-through">{originalPrice.toFixed(2)} TND</p>
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              -{discountPercentage}%
            </span>
          </>
        ) : (
          <p className="text-2xl font-bold text-gray-900">{product.prixVenteTTC?.toFixed(2)} TND</p>
        )}
      </div>

      {stockRequired && (
      <div className="flex items-center">
        <div className={`flex-shrink-0 h-3 w-3 rounded-full ${
          // For products without variants, use product.stock
          (!product.isVariant || !product.productVariants || product.productVariants.length === 0)
            ? (product.stock > 0
                ? (product.stock <= 5 ? 'bg-amber-500' : 'bg-emerald-500')
                : 'bg-red-500')
            : (selectedVariant && selectedVariant.stock > 0)
                ? (selectedVariant.stock <= 5 ? 'bg-amber-500' : 'bg-emerald-500')
                : 'bg-red-500'
          } mr-2`}></div>
        <p className={`text-sm font-medium ${stockLabelColor()}`}>
          {getStockLabel()}
        </p>
      </div>
      )}

      {/* Product Description - Hidden on mobile */}
      <div className="hidden md:block mt-4 border-t border-b border-gray-200 py-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Description</h3>
        <div className="prose prose-sm max-w-none text-gray-600">
          {product.description ? (
            <p>{product.description}</p>
          ) : (
            <p className="text-gray-500 italic">Aucune description disponible pour ce produit.</p>
          )}
        </div>
      </div>

      {/* Pack Selection - shown before variant options */}
      {product.packProducts && product.packProducts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700">Offres Pack</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {/* No-pack option (single unit) */}
            <button
              type="button"
              onClick={() => { setSelectedPack(null); setPackVariantSelections([]); }}
              className={`flex items-center justify-between p-3 border rounded-lg text-left transition-all ${
                !selectedPack
                  ? 'border-[#174540] bg-[#174540]/5 ring-1 ring-[#174540]'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div>
                <p className="text-sm font-medium text-gray-900">1 unité</p>
                <p className="text-xs text-gray-500">Prix normal</p>
              </div>
              <span className="text-sm font-bold text-gray-900">{productUnitPrice.toFixed(2)} TND</span>
            </button>
            {product.packProducts.map((pack, idx) => {
              const packTotal = pack.qte * pack.prixUnitaireTTC;
              const normalTotal = pack.qte * productUnitPrice;
              const savings = normalTotal - packTotal;
              const isSelected = selectedPack && selectedPack.qte === pack.qte;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handlePackSelect(pack)}
                  className={`flex items-center justify-between p-3 border rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-[#174540] bg-[#174540]/5 ring-1 ring-[#174540]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">Pack de {pack.qte}</p>
                    <p className="text-xs text-gray-500">{pack.prixUnitaireTTC.toFixed(2)} TND/unité</p>
                    {savings > 0 && (
                      <p className="text-xs font-medium text-emerald-600 mt-0.5">Économie: {savings.toFixed(2)} TND</p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-gray-900">{packTotal.toFixed(2)} TND</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Variant Selection */}
      {(!selectedPack) && renderVariantOptions()}

      {/* Per-unit variant selection for packs */}
      {selectedPack && product.isVariant && product.productVariants && product.productVariants.length > 0 && (() => {
        const hasMultiType = product.productVariants.some(v => v.name.includes(' - '));
        if (hasMultiType) {
          const colorSet = new Set();
          const sizeSet = new Set();
          product.productVariants.forEach(v => {
            const parts = v.name.split(' - ');
            if (parts.length === 2) { colorSet.add(parts[0]); sizeSet.add(parts[1]); }
          });
          const colors = Array.from(colorSet);
          const sizes = Array.from(sizeSet);

          const getCombinationStatus = (color, size) => {
            const variant = product.productVariants.find(v => v.name === `${color} - ${size}`);
            if (!variant) return 'unavailable';
            if (stockRequired && variant.stock <= 0) return 'outOfStock';
            return 'available';
          };

          return (
            <div className="space-y-4">
              {Array.from({ length: selectedPack.qte }).map((_, unitIdx) => {
                const selectedId = packVariantSelections[unitIdx];
                const selectedUnitVariant = selectedId ? product.productVariants.find(v => v.id === selectedId) : null;
                const unitParts = selectedUnitVariant ? selectedUnitVariant.name.split(' - ') : ['', ''];
                const [unitColor, unitSize] = unitParts;

                return (
                  <div key={unitIdx} className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                    <p className="text-sm font-semibold text-gray-800">Produit {unitIdx + 1}</p>
                    {colors.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Couleur</h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {colors.map(color => {
                            const hasAvailable = sizes.some(s => getCombinationStatus(color, s) === 'available');
                            return (
                              <button
                                key={color}
                                type="button"
                                onClick={() => {
                                  const validSize = sizes.find(s => getCombinationStatus(color, s) === 'available') || sizes[0];
                                  const target = product.productVariants.find(v => v.name === `${color} - ${validSize}`);
                                  if (target) handlePackVariantSelect(unitIdx, target.id);
                                }}
                                className={`px-3 py-1.5 text-sm border rounded-md ${color === unitColor
                                  ? 'border-[#174540] bg-[#174540] text-white'
                                  : hasAvailable
                                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                disabled={!hasAvailable}
                              >
                                {color}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {sizes.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700">Taille</h3>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {sizes.map(size => {
                            const status = getCombinationStatus(unitColor, size);
                            const isAvailable = status === 'available';
                            const isSelected = size === unitSize;
                            return (
                              <button
                                key={size}
                                type="button"
                                onClick={() => {
                                  if (!isAvailable) return;
                                  const target = product.productVariants.find(v => v.name === `${unitColor} - ${size}`);
                                  if (target) handlePackVariantSelect(unitIdx, target.id);
                                }}
                                className={`px-3 py-1.5 text-sm border rounded-md ${isSelected
                                  ? 'border-[#174540] bg-[#174540] text-white'
                                  : isAvailable
                                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                disabled={!isAvailable}
                              >
                                {size}
                                {status === 'outOfStock' && <span className="ml-1 text-xs">(Épuisé)</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {errors.packVariants && (
                <p className="text-xs text-red-600 mt-1">{errors.packVariants}</p>
              )}
            </div>
          );
        } else {
          // Single-type variants for packs: show flat variant buttons per unit
          return (
            <div className="space-y-4">
              {Array.from({ length: selectedPack.qte }).map((_, unitIdx) => {
                const selectedId = packVariantSelections[unitIdx];
                return (
                  <div key={unitIdx} className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-gray-800">Produit {unitIdx + 1}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.productVariants.filter(v => !stockRequired || v.stock > 0).map(variant => (
                        <button
                          key={variant.id}
                          type="button"
                          onClick={() => handlePackVariantSelect(unitIdx, variant.id)}
                          className={`px-2.5 py-1 text-xs border rounded-md transition-colors ${
                            selectedId === variant.id
                              ? 'border-[#174540] bg-[#174540] text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {variant.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {errors.packVariants && (
                <p className="text-xs text-red-600 mt-1">{errors.packVariants}</p>
              )}
            </div>
          );
        }
      })()}

      {/* Quantity selector - hide when pack is selected */}
      {!selectedPack && (
      <div className="flex items-center">
        <span className="mr-3 text-sm font-medium text-gray-700">Quantité</span>
        <div className="flex items-center border border-gray-300 rounded-md">
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            disabled={quantity <= 1}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
            </svg>
          </button>
          <span className="w-10 text-center">{quantity}</span>
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={() => onQuantityChange(Math.min(maxOrderQuantity, quantity + 1))}
            disabled={
              product.isVariant
                ? (!selectedVariant || quantity >= maxOrderQuantity)
                : (quantity >= maxOrderQuantity)
            }
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>
      )}

      {/* Delivery info banner */}
      <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-lg">
        <div className="flex">
          <svg className="h-5 w-5 text-green-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="ml-3">
            <p className="text-sm text-green-700">
              {isFreeDelivery
                ? <span className="font-medium">Livraison gratuite ! Cet avantage est appliqué à votre commande.</span>
                : freeDeliveryEnabled && freeDeliveryThreshold > 0
                  ? <span>Livraison <span className="font-medium">{deliveryFee?.toFixed(2)} TND</span>. Gratuite dès <span className="font-medium">{freeDeliveryThreshold} article{freeDeliveryThreshold > 1 ? 's' : ''}</span> commandé{freeDeliveryThreshold > 1 ? 's' : ''}.</span>
                  : <span>Livraison <span className="font-medium">{deliveryFee?.toFixed(2)} TND</span>.</span>
              }
            </p>
          </div>
        </div>
      </div>

      {/* Client Information Form */}
      <div className="mt-8 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de commande</h3>

        <form onSubmit={handleOrderSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
            {/* First Name */}
            <div className="sm:col-span-3">
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                Prénom (الاسم) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.firstName
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                    } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                  placeholder="Votre prénom"
                />
              </div>
              {errors.firstName && (
                <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="sm:col-span-3">
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Nom (اللقب) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.lastName
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                    } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                  placeholder="Votre nom"
                />
              </div>
              {errors.lastName && (
                <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Email */}
            <div className="sm:col-span-3">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email (البريد الإلكتروني)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.email
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                    } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                  placeholder="exemple@email.com"
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="sm:col-span-3">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Téléphone (رقم الهاتف) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlinePhone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  ref={phoneInputRef}
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={8} // Added this line to limit to 8 digits
                  className={`pl-10 block w-full py-3 border ${errors.phone
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                    } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                  placeholder="Votre numéro (8 chiffres)"
                />
              </div>
              {errors.phone && (
                <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Address */}
            <div className="sm:col-span-6">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                Adresse (العنوان) <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <HiOutlineLocationMarker className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="address"
                  id="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`pl-10 block w-full py-3 border ${errors.address
                      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                    } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                  placeholder="Adresse complète"
                />
              </div>
              {errors.address && (
                <p className="mt-2 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* City - Replace the existing city select with this */}
<div className="sm:col-span-3">
  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
    Ville (المدينة) <span className="text-red-500">*</span>
  </label>
  <div className="mt-1 relative rounded-md shadow-sm">
    <CustomDropdown
      name="city"
      value={formData.city}
      onChange={handleChange}
      options={tunisianCities}
      placeholder="Sélectionnez une ville"
      searchPlaceholder="Rechercher une ville..."
      error={!!errors.city}
      icon={HiOutlineOfficeBuilding}
      valueKey="value"
      labelKey="label"
    />
  </div>
  {errors.city && (
    <p className="mt-2 text-sm text-red-600">{errors.city}</p>
  )}
</div>

            {/* Coupon / Promo Code */}
            <div className="sm:col-span-3 relative">
              <label className="block text-sm font-medium text-gray-700">
                Code promo / Coupon (رمز التخفيض)
              </label>

              {/* If a spin coupon is applied, show it as a tag */}
              {promoCodeState.isValid && promoCodeState.type === 'spin' ? (
                <div className="mt-1 flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5">
                    <span className="text-base">🎰</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono font-bold text-emerald-800 truncate">{promoCodeState.validatedCode?.code}</p>
                      <p className="text-xs text-emerald-600">-{promoCodeState.pourcentage}% sur 1 article (-{promoCodeState.discount.toFixed(2)} TND)</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, codePromo: '' }));
                      setPromoCodeState({ isValidating: false, isValid: false, discount: 0, validatedCode: null, type: null, pourcentage: 0, error: null });
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors flex-shrink-0"
                    title="Retirer le coupon"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="mt-1 space-y-2">
                  {/* Manual promo code input */}
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlineMailOpen className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="codePromo"
                      id="codePromo"
                      value={formData.codePromo}
                      onChange={handleChange}
                      onBlur={handlePromoCodeBlur}
                      className={`pl-10 block w-full py-3 border ${
                        errors.codePromo
                          ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
                          : promoCodeState.isValid
                          ? 'border-green-300 text-green-900 focus:ring-green-500 focus:border-green-500'
                          : 'border-gray-300 focus:ring-[#174540] focus:border-[#174540]'
                      } rounded-md shadow-sm placeholder-gray-400 sm:text-sm transition-all`}
                      placeholder="Code promo (optionnel)"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      {promoCodeState.isValidating && (
                        <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {promoCodeState.isValid && !promoCodeState.isValidating && (
                        <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {promoCodeState.error && (
                        <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* Coupon picker button — only for authenticated clients */}
                  {isAuthenticated() && getCurrentUser()?.type === 'Client' && (
                    <button
                      type="button"
                      onClick={openCouponPicker}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md text-sm font-medium transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                      Utiliser un coupon roulette 🎰
                    </button>
                  )}
                </div>
              )}

              {errors.codePromo && (
                <p className="mt-2 text-sm text-red-600">{errors.codePromo}</p>
              )}
              {promoCodeState.isValid && promoCodeState.type === 'promo' && (
                <p className="mt-2 text-sm text-green-600">
                  Code promo valide ! Réduction de {promoCodeState.discount.toFixed(2)} TND appliquée.
                </p>
              )}
              {promoCodeState.error && !errors.codePromo && (
                <p className="mt-2 text-sm text-red-600">{promoCodeState.error}</p>
              )}

              {/* Coupon picker dropdown */}
              <AnimatePresence>
                {showCouponPicker && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowCouponPicker(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
                    >
                      <div className="px-3 py-2 border-b border-gray-100 bg-emerald-50">
                        <p className="text-xs font-semibold text-emerald-700">Mes coupons actifs</p>
                      </div>
                      {couponsLoading ? (
                        <div className="flex justify-center py-5">
                          <svg className="animate-spin h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        </div>
                      ) : myCoupons.length === 0 ? (
                        <div className="px-4 py-5 text-center">
                          <p className="text-sm text-gray-500">Aucun coupon actif.</p>
                          <p className="text-xs text-gray-400 mt-1">Jouez à la roulette pour en gagner !</p>
                        </div>
                      ) : (
                        <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                          {myCoupons.map((coupon) => {
                            const unitPrice = promoActive ? promoPriceVal : Number(product?.prixVenteTTC ?? product?.prixVenteHt ?? 0);
                            const disc = Number((unitPrice * coupon.pourcentage / 100).toFixed(2));
                            return (
                              <li key={coupon.id}>
                                <button
                                  type="button"
                                  onClick={() => applyCoupon(coupon)}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left"
                                >
                                  <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-sm font-bold text-emerald-700">{coupon.pourcentage}%</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-mono font-bold text-gray-800">{coupon.code}</p>
                                    <p className="text-xs text-gray-500">-{disc.toFixed(2)} TND · Expire le {new Date(coupon.expirationDate).toLocaleDateString('fr-FR')}</p>
                                  </div>
                                  <svg className="h-4 w-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* MOVED: Order summary - now below the client information form */}
          <div className="mt-8 p-6 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="font-medium text-gray-900 mb-4">Résumé de la commande</h4>
            <div className="flex justify-between mb-2">
              <span>Prix unitaire:</span>
              <span>{effectiveUnitPrice.toFixed(2)} TND</span>
            </div>
            {selectedPack && (
              <div className="flex justify-between mb-2 text-emerald-600 text-sm">
                <span>Pack de {selectedPack.qte} sélectionné</span>
                <span className="line-through text-gray-400 mr-1">{productUnitPrice.toFixed(2)} TND</span>
              </div>
            )}
            <div className="flex justify-between mb-2">
              <span>Quantité:</span>
              <span>{effectiveQuantity}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Sous-total:</span>
              <span>{productTotal.toFixed(2)} TND</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Livraison:</span>
              <span>{isFreeDelivery ? 'Gratuite' : `${finalDeliveryFee?.toFixed(2)} TND`}</span>
            </div>
            
            {/* Promo discount line */}
            {promoCodeState.isValid && promoDiscount > 0 && (
              <div className="flex justify-between mb-2 text-green-600">
                <span>Réduction (Code promo):</span>
                <span>-{promoDiscount.toFixed(2)} TND</span>
              </div>
            )}
            
            <div className="border-t border-gray-200 mt-4 pt-4 flex justify-between font-medium">
              <span>Total:</span>
              <span>{grandTotal.toFixed(2)} TND</span>
            </div>
          </div>

          {/* Error message */}
          {orderError && (
            <div className="rounded-md bg-red-50 p-4 mt-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{orderError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success message */}
          {orderSuccess && (
            <div className="rounded-md bg-green-50 p-4 mt-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">Commande créée avec succès! Nous vous contacterons bientôt.</p>
                </div>
              </div>
            </div>
          )}

          {/* Submit buttons */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            {/* Ajouter au panier button */}
            <button
              type="button"
              onClick={onAddToCart}
              disabled={cannotOrder}
              className={`flex items-center justify-center px-8 py-3 border text-base font-medium rounded-md
                ${cannotOrder
                  ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                  : 'border-[#174540] bg-white text-[#174540] hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] transition-colors'
                }`}
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              Ajouter au panier
            </button>
            
            {/* Commander maintenant button */}
            <button
              type="submit"
              disabled={isSubmitting || cannotOrder}
              className={`flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md
                ${(isSubmitting || cannotOrder)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'text-white bg-[#174540] hover:bg-[#0f2e2a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] transition-colors'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Traitement...
                </>
              ) : 'Commander maintenant'}
            </button>
          </div>
        </form>
      </div>

      {/* Added to cart notification */}
      <AnimatePresence>
        {showAddedToCart && (
          <motion.div
            variants={cartNotification}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-6 right-6 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center z-50"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Produit ajouté au panier</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Roulette button */}
      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setShowRoulette(true)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-xl text-white font-semibold text-sm"
        style={{ background: 'linear-gradient(135deg, #174540 0%, #2a7a6f 100%)' }}
        title="Tentez votre chance à la roulette !"
      >
        <span className="text-lg leading-none">🎰</span>
        <span>Roulette</span>
      </motion.button>

      {/* Roulette Wheel Modal */}
      <RouletteWheel
        isOpen={showRoulette}
        onClose={() => setShowRoulette(false)}
        onNeedLogin={() => {
          setShowRoulette(false);
          setShowAuthPopup(true);
        }}
      />

      {/* Auth Popup (login/register before spinning) */}
      <AuthPopup
        isOpen={showAuthPopup}
        onClose={() => setShowAuthPopup(false)}
        onSuccess={() => {
          setShowAuthPopup(false);
          setTimeout(() => setShowRoulette(true), 500);
        }}
      />
    </div>
  );
};

export const MobileProductHeader = ({ product, selectedVariant }) => {
  return (
    <div className="block md:hidden mb-4 px-4">
      {/* Product name */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">{product.name}</h1>
        
        {product.reference && (
          <p className="mt-1 text-sm text-gray-500">
            Réf: {selectedVariant ? selectedVariant.reference : product.reference}
          </p>
        )}
      </div>

      {/* Product Description */}
      <div className="mt-3 border-b border-gray-200 pb-3">
        <div className="prose prose-sm max-w-none text-gray-600">
          {product.description ? (
            <p className="text-sm leading-relaxed">{product.description}</p>
          ) : (
            <p className="text-gray-500 italic text-sm">Aucune description disponible pour ce produit.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;